'use client';

import * as React from 'react';

import { CheckIcon, ChevronsUpDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import * as RPNInput from 'react-phone-number-input';
import flags from 'react-phone-number-input/flags';

import { cn } from '../lib/utils';
import { Button } from '../shadcn/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../shadcn/command';
import { Input } from '../shadcn/input';
import { Popover, PopoverContent, PopoverTrigger } from '../shadcn/popover';
import { ScrollArea } from '../shadcn/scroll-area';
import { useTenantCurrency } from './currency-icon';

/**
 * Default phone-input country derived from the tenant's currency. A currency
 * isn't a country, but for our single-country Gulf/Levant tenants the mapping
 * is unambiguous and lets the input pre-select the right calling code from the
 * existing currency setting — no separate "country" config needed.
 *
 * Keep in sync with `currencyToDefaultCountry` in `@kit/shared/currency`
 * (the server-side source of truth used by routes without the currency
 * context, e.g. /auth/*). Anything unmapped falls back to 'SA'.
 */
const CURRENCY_DEFAULT_COUNTRY: Record<string, RPNInput.Country> = {
  SAR: 'SA',
  AED: 'AE',
  JOD: 'JO',
};

const FALLBACK_PHONE_COUNTRY: RPNInput.Country = 'SA';

function currencyToCountry(
  currency: string | null | undefined,
): RPNInput.Country {
  if (!currency) return FALLBACK_PHONE_COUNTRY;
  return (
    CURRENCY_DEFAULT_COUNTRY[currency.toUpperCase()] ?? FALLBACK_PHONE_COUNTRY
  );
}

type PhoneInputProps = Omit<
  React.ComponentProps<'input'>,
  'onChange' | 'value' | 'ref'
> &
  Omit<RPNInput.Props<typeof RPNInput.default>, 'onChange'> & {
    onChange?: (value: RPNInput.Value) => void;
    /**
     * Extra classes for the digits <input> itself (not the wrapper — `className`
     * targets that). Use to tune per-call-site padding/height without altering
     * the shared default for every other PhoneInput in the app.
     */
    inputClassName?: string;
    popoverClassName?: string;
    popoverStyle?: React.CSSProperties;
  };

/**
 * PhoneInput - A comprehensive phone number input component with international support
 *
 * @description
 * This component provides a professional phone input experience with:
 * - International phone number validation using react-phone-number-input
 * - Country code selector with flag icons
 * - Automatic formatting and validation
 * - Support for all countries worldwide
 * - Form integration support
 * - Accessibility features
 *
 * @example
 * Basic usage:
 * ```tsx
 * import { PhoneInput } from '@kit/ui/phone-input';
 *
 * function MyForm() {
 *   const [phone, setPhone] = useState('');
 *
 *   return (
 *     <PhoneInput
 *       value={phone}
 *       onChange={setPhone}
 *       defaultCountry="SA"
 *       placeholder="Enter phone number"
 *     />
 *   );
 * }
 * ```
 *
 * @example
 * With React Hook Form:
 * ```tsx
 * import { PhoneInput } from '@kit/ui/phone-input';
 * import { FormField, FormItem, FormLabel, FormControl, FormMessage } from '@kit/ui/form';
 *
 * <FormField
 *   name="phoneNumber"
 *   render={({ field }) => (
 *     <FormItem>
 *       <FormLabel>Phone Number</FormLabel>
 *       <FormControl>
 *         <PhoneInput
 *           value={field.value}
 *           onChange={field.onChange}
 *           onBlur={field.onBlur}
 *           defaultCountry="SA"
 *           placeholder="Enter phone number"
 *         />
 *       </FormControl>
 *       <FormMessage />
 *     </FormItem>
 *   )}
 * />
 * ```
 *
 * @example
 * With validation:
 * ```tsx
 * import { PhoneInput } from '@kit/ui/phone-input';
 * import { isValidPhoneNumber } from 'react-phone-number-input';
 *
 * function ValidatedPhoneInput() {
 *   const [phone, setPhone] = useState('');
 *   const [error, setError] = useState('');
 *
 *   const handleChange = (value) => {
 *     setPhone(value);
 *     if (value && !isValidPhoneNumber(value)) {
 *       setError('Please enter a valid phone number');
 *     } else {
 *       setError('');
 *     }
 *   };
 *
 *   return (
 *     <div>
 *       <PhoneInput
 *         value={phone}
 *         onChange={handleChange}
 *         defaultCountry="SA"
 *       />
 *       {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
 *     </div>
 *   );
 * }
 * ```
 */
const PhoneInput: React.ForwardRefExoticComponent<PhoneInputProps> =
  React.forwardRef<React.ElementRef<typeof RPNInput.default>, PhoneInputProps>(
    (
      {
        className,
        inputClassName,
        popoverClassName,
        popoverStyle,
        countrySelectProps,
        onChange,
        value,
        countries,
        defaultCountry,
        ...props
      },
      ref,
    ) => {
      // If no countries prop is provided, use all countries except Israel by default
      const filteredCountries = React.useMemo(() => {
        if (countries) {
          return countries; // Use provided countries as-is
        }
        // Default: exclude Israel from all countries
        return RPNInput.getCountries().filter((country) => country !== 'IL');
      }, [countries]);

      // When the caller doesn't pin a country, derive it from the tenant's
      // currency (SAR→SA, AED→AE, JOD→JO) read from TenantCurrencyContext.
      // An explicit `defaultCountry` prop always wins. Routes outside
      // (tenant)/layout.tsx (e.g. /auth/*) mount their own
      // TenantCurrencyProvider so the same derivation works there too.
      const tenantCurrency = useTenantCurrency();
      const resolvedDefaultCountry =
        defaultCountry ?? currencyToCountry(tenantCurrency);

      return (
        <RPNInput.default
          ref={ref}
          // Phone numbers (and their country-calling-code prefix) are always
          // read left-to-right. Force LTR so the country selector stays on the
          // left and the digits don't get flipped/right-aligned inside an RTL
          // (Arabic) page. `dir` must go on the CONTAINER via
          // containerComponentProps — rest props (a bare `dir`) are forwarded
          // to the digits <input>, which leaves the flex row itself RTL.
          dir="ltr"
          containerComponentProps={{ dir: 'ltr' }}
          className={cn('flex', className)}
          flagComponent={FlagComponent}
          countrySelectComponent={CountrySelect}
          countrySelectProps={{
            ...countrySelectProps,
            popoverClassName,
            popoverStyle,
          }}
          inputComponent={InputComponent}
          smartCaret={false}
          withCountryCallingCode={false}
          international={false}
          countries={filteredCountries}
          defaultCountry={resolvedDefaultCountry}
          // Reaches the digits <input> (InputComponent merges it over the base
          // Input classes). Only set when a caller passes inputClassName, so
          // every other PhoneInput keeps its default padding/height.
          numberInputProps={
            inputClassName ? { className: inputClassName } : undefined
          }
          value={value || undefined}
          /**
           * Handles the onChange event.
           *
           * react-phone-number-input might trigger the onChange event as undefined
           * when a valid phone number is not entered. To prevent this,
           * the value is coerced to an empty string.
           *
           * @param {E164Number | undefined} value - The entered value
           */
          onChange={(value) => onChange?.(value || ('' as RPNInput.Value))}
          {...props}
        />
      );
    },
  );
PhoneInput.displayName = 'PhoneInput';

const InputComponent = React.forwardRef<
  HTMLInputElement,
  React.ComponentProps<'input'>
>(({ className, ...props }, ref) => (
  <Input
    className={cn('rounded-s-none rounded-e-lg', className)}
    {...props}
    ref={ref}
  />
));
InputComponent.displayName = 'InputComponent';

type CountryEntry = { label: string; value: RPNInput.Country | undefined };

type CountrySelectProps = {
  popoverClassName?: string;
  popoverStyle?: React.CSSProperties;
  disabled?: boolean;
  value: RPNInput.Country;
  options: CountryEntry[];
  onChange: (country: RPNInput.Country) => void;
};

const CountrySelect = ({
  popoverClassName,
  popoverStyle,
  disabled,
  value: selectedCountry,
  options: countryList,
  onChange,
}: CountrySelectProps) => {
  const [searchValue, setSearchValue] = React.useState('');
  const [isOpen, setIsOpen] = React.useState(false);
  const { t } = useTranslation('common');

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen} modal>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="flex gap-1 rounded-s-lg rounded-e-none border-r-0 px-3 focus:z-10"
          disabled={disabled}
        >
          <FlagComponent
            country={selectedCountry}
            countryName={selectedCountry}
          />
          <span className="text-sm font-medium">
            +
            {selectedCountry
              ? RPNInput.getCountryCallingCode(selectedCountry)
              : '966'}
          </span>
          <ChevronsUpDown
            className={cn(
              '-mr-2 size-4 opacity-50',
              disabled ? 'hidden' : 'opacity-100',
            )}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn('w-[300px] p-0', popoverClassName)}
        style={popoverStyle}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <Command>
          <CommandInput
            value={searchValue}
            onValueChange={setSearchValue}
            placeholder={t('searchCountryPlaceholder')}
            className="text-base"
          />
          <CommandList>
            <ScrollArea className="h-72">
              <CommandEmpty>{t('noCountryFound')}</CommandEmpty>
              <CommandGroup>
                {countryList.map(({ value, label }) =>
                  value ? (
                    <CountrySelectOption
                      key={value}
                      country={value}
                      countryName={label}
                      selectedCountry={selectedCountry}
                      onChange={onChange}
                      onSelectComplete={() => setIsOpen(false)}
                    />
                  ) : null,
                )}
              </CommandGroup>
            </ScrollArea>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

interface CountrySelectOptionProps extends RPNInput.FlagProps {
  selectedCountry: RPNInput.Country;
  onChange: (country: RPNInput.Country) => void;
  onSelectComplete: () => void;
}

const CountrySelectOption = ({
  country,
  countryName,
  selectedCountry,
  onChange,
  onSelectComplete,
}: CountrySelectOptionProps) => {
  const handleSelect = () => {
    onChange(country);
    onSelectComplete();
  };

  return (
    <CommandItem className="gap-2" onSelect={handleSelect}>
      <FlagComponent country={country} countryName={countryName} />
      <span className="flex-1 text-sm">{countryName}</span>
      <span className="text-foreground/50 text-sm">{`+${RPNInput.getCountryCallingCode(country)}`}</span>
      <CheckIcon
        className={`ml-auto size-4 ${country === selectedCountry ? 'opacity-100' : 'opacity-0'}`}
      />
    </CommandItem>
  );
};

const FlagComponent = ({ country, countryName }: RPNInput.FlagProps) => {
  // Handle undefined country (fallback to Saudi Arabia)
  if (!country) {
    return (
      <span
        className="flex h-4 w-6 items-center justify-center text-sm"
        title="Saudi Arabia"
      >
        🇸🇦
      </span>
    );
  }

  // Use Apple emoji for Saudi Arabia, SVG flags for others
  if (country === 'SA') {
    return (
      <span
        className="flex h-4 w-6 items-center justify-center text-sm"
        title={countryName}
      >
        🇸🇦
      </span>
    );
  }

  const Flag = flags[country];

  return (
    <span className="bg-foreground/20 flex h-4 w-6 overflow-hidden rounded-sm [&_svg:not([class*='size-'])]:size-full">
      {Flag && <Flag title={countryName} />}
    </span>
  );
};

export { PhoneInput };

// Export additional utilities for validation
export {
  isValidPhoneNumber,
  formatPhoneNumber,
  getCountryCallingCode,
} from 'react-phone-number-input';
